import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactElement,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { AppIcons, type AppIconName } from './icons';

/*
 * IconButton — VOLL DS v2 icon-only button.
 *
 * Examples:
 *   <IconButton icon="settings" variant="ghost" size="sm" aria-label="Settings" onClick={open} />
 *   <IconButton icon="plus" variant="primary" aria-label="Add project" />
 *   <IconButton asChild icon="plus" aria-label="New project">
 *     <Link to="/orgs/$orgSlug/project/new" params={{ orgSlug }} />
 *   </IconButton>
 *
 * Sizes match `.btn`/`.btn-icon` from the mockup: xs 22, sm 26, default 30,
 * lg 36 (square). Variant set mirrors Button so the two atoms stay in sync.
 *
 * Locked: no `className`, no children. `aria-label` is required so the
 * icon-only target ships with a name. To render as a link, use `asChild`
 * with a `<Link>` child.
 */
const iconButtonVariants = cva(
  'inline-flex items-center justify-center border rounded-md font-medium ' +
    'transition-colors duration-fast ease-standard ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    'focus-visible:outline-none',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground border-primary ' +
          'hover:bg-primary-hover hover:border-primary-hover active:bg-primary-press',
        outline: 'bg-card text-foreground border-input hover:bg-muted hover:border-border-strong',
        ghost: 'bg-transparent text-foreground border-transparent hover:bg-muted',
        subtle: 'bg-muted text-foreground border-transparent hover:bg-accent',
        danger: 'bg-transparent text-tone-red-fg border-transparent hover:bg-tone-red-tint',
      },
      size: {
        xs: 'h-5.5 w-5.5',
        sm: 'h-row-h-compact w-row-h-compact',
        default: 'h-row-h w-row-h',
        lg: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'default',
    },
  },
);

type IconButtonVariantProps = VariantProps<typeof iconButtonVariants>;

const ICON_SIZE: Record<NonNullable<IconButtonVariantProps['size']>, number> = {
  xs: 12,
  sm: 14,
  default: 14,
  lg: 16,
};

type BaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> &
  IconButtonVariantProps & {
    icon: AppIconName;
    'aria-label': string;
  };

type AsChildProps = BaseProps & {
  asChild: true;
  children: ReactElement;
};

type ButtonElProps = BaseProps & {
  asChild?: false;
  children?: never;
};

type IconButtonProps = AsChildProps | ButtonElProps;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant, size, icon, asChild = false, type, children, ...props },
  ref,
) {
  const iconSize = ICON_SIZE[size ?? 'default'];
  const className = iconButtonVariants({ variant, size });
  const iconEl = <AppIcons name={icon} size={iconSize} />;

  if (asChild) {
    if (!isValidElement(children)) {
      throw new Error('IconButton asChild requires a single React element child.');
    }
    return (
      <Slot ref={ref} className={className} {...props}>
        {cloneElement(children, {}, iconEl)}
      </Slot>
    );
  }

  return (
    <button ref={ref} type={type ?? 'button'} className={className} {...props}>
      {iconEl}
    </button>
  );
});
